import {
  AlertTriangle,
  BarChart3,
  TrendingUp,
  Banknote,
  CheckSquare,
  ChevronsUpDown,
  Briefcase,
  CreditCard,
  Download,
  Filter,
  FileText,
  Inbox,
  LayoutDashboard,
  ListTodo,
  MessageSquare,
  Package,
  Plug,
  Receipt,
  Repeat,
  Settings,
  Tag,
  Target,
  Ticket,
  UserCheck,
  Users,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { cn } from '@control-center/ui';

import { useAuth } from '../../hooks/use-auth';
import { useMyPendingTasksCount } from '../../hooks/use-crm-activities';
import { useFailedProvisioningCount } from '../../hooks/use-provisioning';

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
  /** Quando true, esconde a seção pra roles sem privilégio admin. */
  adminOnly?: boolean;
}

const sections: NavSection[] = [
  {
    title: 'Início',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/reports', icon: BarChart3, label: 'Relatórios' },
      { to: '/reports/financial', icon: TrendingUp, label: 'Financeiro (MRR/Churn)' },
    ],
  },
  {
    title: 'CRM',
    items: [
      { to: '/crm/leads', icon: Target, label: 'Leads' },
      { to: '/crm/deals', icon: Briefcase, label: 'Deals' },
      { to: '/crm/pipeline', icon: Filter, label: 'Pipeline' },
      { to: '/crm/proposals', icon: FileText, label: 'Propostas' },
      { to: '/crm/activities/my-tasks', icon: ListTodo, label: 'Minhas tarefas' },
    ],
  },
  {
    title: 'Clientes',
    items: [
      { to: '/identities', icon: Users, label: 'Clientes' },
      { to: '/subscriptions', icon: Repeat, label: 'Assinaturas' },
      { to: '/communications', icon: MessageSquare, label: 'Comunicação' },
    ],
  },
  {
    title: 'Faturamento',
    items: [
      { to: '/charges', icon: Banknote, label: 'Cobranças' },
      { to: '/fiscal', icon: Receipt, label: 'Notas fiscais' },
      { to: '/asaas-accounts', icon: CreditCard, label: 'Contas Asaas' },
    ],
  },
  {
    title: 'Catálogo',
    items: [
      { to: '/catalog', icon: Package, label: 'Catálogo SaaS' },
      { to: '/plans', icon: Tag, label: 'Planos comerciais' },
      { to: '/coupons', icon: Ticket, label: 'Cupons' },
    ],
  },
  {
    title: 'Integrações',
    items: [
      { to: '/integrations', icon: Plug, label: 'SaaS conectados' },
      { to: '/provisioning', icon: CheckSquare, label: 'Provisioning' },
      { to: '/webhook-events', icon: Webhook, label: 'Eventos recebidos' },
    ],
  },
  {
    title: 'Administração',
    adminOnly: true,
    items: [
      { to: '/admin/notification-templates', icon: FileText, label: 'Templates' },
      { to: '/admin/notifications/dlq', icon: Inbox, label: 'Mensagens falhadas' },
      { to: '/admin/divergence-alerts', icon: AlertTriangle, label: 'Divergências' },
      { to: '/identities/awaiting-review', icon: UserCheck, label: 'Vínculos a revisar' },
      { to: '/identities/import', icon: Download, label: 'Importar de SaaS' },
    ],
  },
];

function initials(name: string): string {
  return (
    name
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '·'
  );
}

function NavLine({ item }: { item: NavItem }): JSX.Element {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          'flex w-full items-center gap-[10px] rounded-[6px] px-3 py-2 text-[13px] font-medium transition-colors',
          isActive ? 'bg-panel-3 text-ink' : 'text-ink-2 hover:bg-panel-2',
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon
            size={14}
            strokeWidth={1.8}
            className={cn('shrink-0', isActive ? 'text-ink' : 'text-muted')}
          />
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && (
            <span className={cn('tnum text-[11px]', isActive ? 'text-ink-2' : 'text-muted-2')}>
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function prettyName(name: string | undefined, email: string | undefined): string {
  if (name && !name.includes('@')) return name;
  const source = email ?? name ?? '';
  const local = source.split('@')[0] ?? '';
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/** Caminho da view "Minhas tarefas" — recebe badge dinâmico de pendentes (AC10). */
const MY_TASKS_PATH = '/crm/activities/my-tasks';
/** Provisioning — recebe badge de falhas na DLQ (Story 12.7 AC7). */
const PROVISIONING_PATH = '/provisioning';

export function Sidebar(): JSX.Element {
  const { user } = useAuth();
  const displayName = prettyName(user?.name, user?.email) || 'Anônimo';
  const displayEmail = user?.email ?? '—';
  const ini = initials(displayName);

  // Badge de tarefas pendentes — só pra quem tem acesso (qualquer role autenticada).
  const { data: pendingTasks } = useMyPendingTasksCount(Boolean(user));
  const tasksBadge = pendingTasks && pendingTasks > 0 ? String(pendingTasks) : undefined;

  // Badge de falhas de provisioning (DLQ) — só admin/financeiro acessam o endpoint.
  const canSeeDlq = user?.role === 'admin' || user?.role === 'financeiro';
  const { data: failedProvisioning } = useFailedProvisioningCount(canSeeDlq);
  const provisioningBadge =
    failedProvisioning && failedProvisioning > 0 ? String(failedProvisioning) : undefined;

  const withBadges = (item: NavItem): NavItem => {
    if (item.to === MY_TASKS_PATH) return { ...item, badge: tasksBadge };
    if (item.to === PROVISIONING_PATH) return { ...item, badge: provisioningBadge };
    return item;
  };

  return (
    <aside className="sticky top-0 flex h-screen flex-col border-r border-line bg-page p-3 pt-4">
      <button
        type="button"
        className="flex w-full items-center gap-[10px] rounded-[10px] border border-line px-3 py-[9px] transition-colors hover:border-line-2"
      >
        <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
          <img src="/logo-opus-ico.png" alt="Opus Owl" className="h-full w-full object-cover" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col text-left leading-[1.1]">
          <span className="truncate text-[13px] font-semibold tracking-[-0.005em]">Opus Owl</span>
          <span className="truncate text-[10px] text-muted-2">Centro de Controle</span>
        </span>
        <ChevronsUpDown size={14} strokeWidth={2} className="text-muted-2" />
      </button>

      <nav className="mt-[14px] flex flex-1 flex-col gap-[2px] overflow-y-auto">
        {sections
          .filter((section) => !section.adminOnly || user?.role === 'admin')
          .map((section, idx) => (
            <div key={section.title} className={cn(idx > 0 && 'mt-[14px]')}>
              <div className="px-3 pb-[6px] text-[11px] font-semibold tracking-wide text-muted-2">
                {section.title}
              </div>
              <div className="flex flex-col gap-[1px]">
                {section.items.map((item) => (
                  <NavLine key={item.to} item={withBadges(item)} />
                ))}
              </div>
            </div>
          ))}

        <div className="my-[14px] mx-2 h-px bg-line" />

        <NavLine item={{ to: '/settings', icon: Settings, label: 'Configurações' }} />
      </nav>

      <div className="mt-auto flex items-center gap-[10px] border-t border-line px-3 py-[10px]">
        <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-gradient-to-br from-muted to-muted-3 text-[11px] font-semibold text-white">
          {ini}
        </span>
        <div className="flex min-w-0 flex-col leading-[1.2]">
          <span className="truncate text-[12px] font-medium">{displayName}</span>
          <span className="truncate text-[11px] text-muted-2">{displayEmail}</span>
        </div>
      </div>
    </aside>
  );
}
